/**
 * AppStats Icon Library
 * Professional SVG icons using lucide-react
 */
/* eslint-disable react-refresh/only-export-components */

import {
  // Navigation Icons
  Map,
  LayoutGrid,
  BarChart3,
  Settings,
  Bell,
  HelpCircle,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  
  // Platform Icons
  Smartphone,
  Laptop,
  Glasses,
  Watch,
  Tv,
  
  // Metric Icons
  Users,
  Activity,
  Eye,
  AlertTriangle,
  Zap,
  TrendingUp,
  TrendingDown,
  Download,
  Star,
  MessageSquare,
  Globe,
  DollarSign,
  
  // Action Icons
  Plus,
  Edit,
  Trash2,
  Share2,
  Upload,
  RefreshCw,
  Search,
  Filter,
  
  // Status Icons
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  Loader2,
  WifiOff,
  Wifi,
  
  // General Icons
  Calendar,
  Clock,
  MapPin,
  ExternalLink,
  Copy,
  LogOut,
  User,
  Mail,
  Lock,
  Unlock,
  
  // App Icons
  Apple,
  Sparkles,
  Target,
  Layers,
  Database,
  Code,
  Terminal,
  FileText,
  
  type LucideIcon,
} from 'lucide-react'

import { ComponentProps } from 'react'

// Icon size variants
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const sizeMap: Record<IconSize, number> = {
  xs: 14,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 28,
  '2xl': 32,
}

interface IconProps extends Omit<ComponentProps<'svg'>, 'size'> {
  size?: IconSize
}

// Create a wrapper for consistent icon styling
export function createIcon(Icon: LucideIcon) {
  return function IconComponent({ 
    size = 'md', 
    className = '',
    ...props 
  }: IconProps) {
    return (
      <Icon 
        size={sizeMap[size]} 
        className={className}
        strokeWidth={2}
        {...props}
      />
    )
  }
}

// Navigation Icons
export const MapIcon = createIcon(Map)
export const GridIcon = createIcon(LayoutGrid)
export const ChartIcon = createIcon(BarChart3)
export const SettingsIcon = createIcon(Settings)
export const BellIcon = createIcon(Bell)
export const HelpIcon = createIcon(HelpCircle)
export const MenuIcon = createIcon(Menu)
export const CloseIcon = createIcon(X)
export const ChevronLeftIcon = createIcon(ChevronLeft)
export const ChevronRightIcon = createIcon(ChevronRight)
export const MoreIcon = createIcon(MoreVertical)

// Platform Icons
export const IOSIcon = createIcon(Smartphone)
export const MacOSIcon = createIcon(Laptop)
export const VisionOSIcon = createIcon(Glasses)
export const WatchOSIcon = createIcon(Watch)
export const TvOSIcon = createIcon(Tv)
/**
 * Android's dome-and-antennae silhouette, drawn to match the stroke weight of
 * the lucide set. The previous `Bot` glyph — a square-headed robot — read as a
 * chatbot rather than Android, which made Android rows in the portfolio look
 * like a different kind of thing entirely.
 */
export function AndroidIcon({ size = 'md', className = '', ...props }: IconProps) {
  return (
    <svg
      width={sizeMap[size]}
      height={sizeMap[size]}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      {/* Antennae */}
      <path d="M7 5 5.5 2.5" />
      <path d="m17 5 1.5-2.5" />
      {/* Dome */}
      <path d="M4 11a8 8 0 0 1 16 0z" />
      {/* Eyes */}
      <path d="M9 8h.01" />
      <path d="M15 8h.01" />
      {/* Body */}
      <path d="M4 13h16v5a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z" />
      {/* Arms */}
      <path d="M1.5 13.5v4" />
      <path d="M22.5 13.5v4" />
    </svg>
  )
}

// Metric Icons
export const UsersIcon = createIcon(Users)
export const ActivityIcon = createIcon(Activity)
export const ViewsIcon = createIcon(Eye)
export const CrashIcon = createIcon(AlertTriangle)
export const EventIcon = createIcon(Zap)
export const TrendUpIcon = createIcon(TrendingUp)
export const TrendDownIcon = createIcon(TrendingDown)
export const DownloadsIcon = createIcon(Download)
export const RatingIcon = createIcon(Star)
export const ReviewIcon = createIcon(MessageSquare)
export const GlobeIcon = createIcon(Globe)
export const RevenueIcon = createIcon(DollarSign)

// Action Icons
export const PlusIcon = createIcon(Plus)
export const EditIcon = createIcon(Edit)
export const DeleteIcon = createIcon(Trash2)
export const ShareIcon = createIcon(Share2)
export const UploadIcon = createIcon(Upload)
export const RefreshIcon = createIcon(RefreshCw)
export const SearchIcon = createIcon(Search)
export const FilterIcon = createIcon(Filter)

// Status Icons
export const SuccessIcon = createIcon(CheckCircle)
export const ErrorIcon = createIcon(XCircle)
export const WarningIcon = createIcon(AlertCircle)
export const InfoIcon = createIcon(Info)
export const LoadingIcon = createIcon(Loader2)
export const OfflineIcon = createIcon(WifiOff)
export const OnlineIcon = createIcon(Wifi)

// General Icons
export const CalendarIcon = createIcon(Calendar)
export const ClockIcon = createIcon(Clock)
export const LocationIcon = createIcon(MapPin)
export const ExternalLinkIcon = createIcon(ExternalLink)
export const CopyIcon = createIcon(Copy)
export const LogOutIcon = createIcon(LogOut)
export const UserIcon = createIcon(User)
export const MailIcon = createIcon(Mail)
export const LockIcon = createIcon(Lock)
export const UnlockIcon = createIcon(Unlock)

// App Icons
export const AppleIcon = createIcon(Apple)
export const SparklesIcon = createIcon(Sparkles)
export const TargetIcon = createIcon(Target)
export const LayersIcon = createIcon(Layers)
export const DatabaseIcon = createIcon(Database)
export const CodeIcon = createIcon(Code)
export const TerminalIcon = createIcon(Terminal)
export const FileIcon = createIcon(FileText)

// Platform icon selector
export function PlatformIcon({ platform, ...props }: IconProps & { platform: string }) {
  switch (platform.toLowerCase()) {
    case 'ios':
      return <IOSIcon {...props} />
    case 'macos':
      return <MacOSIcon {...props} />
    case 'visionos':
      return <VisionOSIcon {...props} />
    case 'watchos':
      return <WatchOSIcon {...props} />
    case 'tvos':
      return <TvOSIcon {...props} />
    case 'android':
      return <AndroidIcon {...props} />
    default:
      // Neutral fallback for unknown/unspecified platforms — avoid implying iOS.
      return <HelpIcon {...props} />
  }
}

// Event type icon selector
export function EventTypeIcon({ type, ...props }: IconProps & { type: string }) {
  switch (type) {
    case 'session_start':
      return <ActivityIcon {...props} />
    case 'session_end':
      return <ClockIcon {...props} />
    case 'screen_view':
      return <ViewsIcon {...props} />
    case 'crash':
      return <CrashIcon {...props} />
    case 'event':
      return <EventIcon {...props} />
    default:
      return <EventIcon {...props} />
  }
}

// Status icon selector
export function StatusIcon({ status, ...props }: IconProps & { status: 'success' | 'error' | 'warning' | 'info' }) {
  switch (status) {
    case 'success':
      return <SuccessIcon {...props} />
    case 'error':
      return <ErrorIcon {...props} />
    case 'warning':
      return <WarningIcon {...props} />
    case 'info':
      return <InfoIcon {...props} />
  }
}
